// components/Reports/AmbientSurfaceConditionTable.js
import React from "react";

/**
 * Reusable component for displaying Ambient & Surface Condition data
 * Used in both Blasting and Painting reports
 *
 * @param {Object} data - Ambient condition data
 * @param {string} data.time - Time/Date
 * @param {number} data.dryBulb - Dry bulb temperature in °C
 * @param {number} data.wetBulb - Wet bulb temperature in °C
 * @param {number} data.humidity - Humidity percentage
 * @param {number} data.dewPoint - Dew point in °C
 * @param {number} data.substrateTemp - Substrate temperature in °C
 * @param {number} data.substrateDewPoint - Substrate temp minus dew point in °C
 * @param {string} data.status - Status (Blast/Paint)
 * @param {string} data.finalInspection - Final inspection notes
 * @param {string} title - Optional custom title (default: "AMBIENT & SURFACE CONDITION")
 */
const AmbientSurfaceConditionTable = ({
  data = {},
  title = "AMBIENT & SURFACE CONDITION",
}) => {
  return (
    <div
      style={{
        marginBottom: "8px",
        pageBreakInside: "avoid",
        WebkitColumnBreakInside: "avoid",
        breakInside: "avoid",
      }}
    >
      {/* Section Header */}
      <div
        style={{
          fontSize: "12px",
          fontWeight: "bold",
          marginBottom: "10px",
          padding: "6px 10px",
          backgroundColor: "#073642",
          color: "#fff",
          letterSpacing: "0.5px",
        }}
        data-pdf-section="ambient-surface-condition-title"
      >
        {title}
      </div>

      {/* Data Table - Two Column Layout */}
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          border: "1px solid #e0e0e0",
        }}
      >
        <tbody>
          {/* Row 1: Time & Dry Bulb */}
          <tr data-pdf-section="ambient-surface-condition-row1-nomargin">
            <td
              style={{
                border: "1px solid #e0e0e0",
                padding: "6px 8px",
                fontWeight: "bold",
                width: "20%",
                backgroundColor: "#f8f9fa",
                fontSize: "10px",
              }}
            >
              Time (Date/Hours)
            </td>
            <td
              style={{
                border: "1px solid #e0e0e0",
                padding: "6px 8px",
                width: "30%",
                backgroundColor: "#fff",
              }}
            >
              {data.time || "—"}
            </td>
            <td
              style={{
                border: "1px solid #e0e0e0",
                padding: "6px 8px",
                fontWeight: "bold",
                width: "20%",
                backgroundColor: "#f8f9fa",
                fontSize: "10px",
              }}
            >
              Dry Bulb
            </td>
            <td
              style={{
                border: "1px solid #e0e0e0",
                padding: "6px 8px",
                width: "30%",
                backgroundColor: "#fff",
              }}
            >
              {data.dryBulb ? `${data.dryBulb} °C` : "—"}
            </td>
          </tr>

          {/* Row 2: Wet Bulb & Humidity */}
          <tr data-pdf-section="ambient-surface-condition-row2-nomargin">
            <td
              style={{
                border: "1px solid #e0e0e0",
                padding: "6px 8px",
                fontWeight: "bold",
                backgroundColor: "#f8f9fa",
                fontSize: "10px",
              }}
            >
              Wet Bulb
            </td>
            <td
              style={{
                border: "1px solid #e0e0e0",
                padding: "6px 8px",
                backgroundColor: "#fff",
              }}
            >
              {data.wetBulb ? `${data.wetBulb} °C` : "—"}
            </td>
            <td
              style={{
                border: "1px solid #e0e0e0",
                padding: "6px 8px",
                fontWeight: "bold",
                backgroundColor: "#f8f9fa",
                fontSize: "10px",
              }}
            >
              Humidity
            </td>
            <td
              style={{
                border: "1px solid #e0e0e0",
                padding: "6px 8px",
                backgroundColor: "#fff",
              }}
            >
              {data.humidity ? `${data.humidity} %` : "—"}
            </td>
          </tr>

          {/* Row 3: Dew Point & Substrate Temp */}
          <tr data-pdf-section="ambient-surface-condition-row3-nomargin">
            <td
              style={{
                border: "1px solid #e0e0e0",
                padding: "6px 8px",
                fontWeight: "bold",
                backgroundColor: "#f8f9fa",
                fontSize: "10px",
              }}
            >
              Dew Point
            </td>
            <td
              style={{
                border: "1px solid #e0e0e0",
                padding: "6px 8px",
                backgroundColor: "#fff",
              }}
            >
              {data.dewPoint ? `${data.dewPoint} °C` : "—"}
            </td>
            <td
              style={{
                border: "1px solid #e0e0e0",
                padding: "6px 8px",
                fontWeight: "bold",
                backgroundColor: "#f8f9fa",
                fontSize: "10px",
              }}
            >
              Substrate Temp
            </td>
            <td
              style={{
                border: "1px solid #e0e0e0",
                padding: "6px 8px",
                backgroundColor: "#fff",
              }}
            >
              {data.substrateTemp ? `${data.substrateTemp} °C` : "—"}
            </td>
          </tr>

          {/* Row 4: Substrate Temp (Minus) Dew Point & Status */}
          <tr data-pdf-section="ambient-surface-condition-row4-nomargin">
            <td
              style={{
                border: "1px solid #e0e0e0",
                padding: "6px 8px",
                fontWeight: "bold",
                backgroundColor: "#f8f9fa",
                fontSize: "10px",
              }}
            >
              Substrate Temp (Minus) Dew Point
            </td>
            <td
              style={{
                border: "1px solid #e0e0e0",
                padding: "6px 8px",
                backgroundColor: "#fff",
              }}
            >
              {data.substrateDewPoint ? `${data.substrateDewPoint} °C` : "—"}
            </td>
            <td
              style={{
                border: "1px solid #e0e0e0",
                padding: "6px 8px",
                fontWeight: "bold",
                backgroundColor: "#f8f9fa",
                fontSize: "10px",
              }}
            >
              Status
            </td>
            <td
              style={{
                border: "1px solid #e0e0e0",
                padding: "6px 8px",
                backgroundColor: "#fff",
              }}
            >
              {data.status || "—"}
            </td>
          </tr>

          {/* Row 5: Final Inspection (Full Width) */}
          <tr data-pdf-section="ambient-surface-condition-row5">
            <td
              style={{
                border: "1px solid #e0e0e0",
                padding: "6px 8px",
                fontWeight: "bold",
                backgroundColor: "#f8f9fa",
                verticalAlign: "top",
                fontSize: "10px",
              }}
            >
              Final Inspection
            </td>
            <td
              colSpan={3}
              style={{
                border: "1px solid #e0e0e0",
                padding: "6px 8px",
                backgroundColor: "#fff",
                whiteSpace: "pre-wrap",
              }}
            >
              {data.finalInspection || "—"}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default AmbientSurfaceConditionTable;
